"use client";

import { useState } from "react";
import Link from "next/link";

import {
  VenueSnapshot,
  conciergeHrefFor,
  conciergePromptFor,
  eventPagePath,
  venueCityPath,
  formatDistance,
  formatEventDateShort,
  formatEventTime,
  ticketPriceText,
  hotelDistance,
  hotelsByDistance,
  isHeroImage,
  liveVenueEvents,
} from "@/lib/eventDealsShared";
import { HotelSummary } from "@/generated/search.openapi";
import HotelCard from "@/components/section/card/HotelCard";
import { GoogleAuthModal } from "@/components/modal/googleAuthModal";
import AboutGondolaModal from "@/components/modal/AboutGondolaModal";
import { H1, H4 } from "@/components/ui/typography";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

type SortKey = "value" | "distance";

type Props = {
  snapshot: VenueSnapshot;
};

export default function VenueHotelsContent({ snapshot }: Props) {
  const [sortBy, setSortBy] = useState<SortKey>("value");
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const venue = snapshot.venue.name ?? snapshot.city_name;
  const hasDistances = Object.keys(snapshot.distances).length > 0;
  const hotels: HotelSummary[] =
    sortBy === "distance" ? hotelsByDistance(snapshot) : snapshot.hotels;
  const events = liveVenueEvents(snapshot);
  const address = [
    snapshot.venue.address_line_1,
    snapshot.venue.city,
    snapshot.venue.state_code,
  ]
    .filter(Boolean)
    .join(", ");
  const nextEvent = events[0] ?? null;
  // A hero-sized photo stretches above the title; a small one (many venues only have a
  // 205x115 photo) sits beside the venue details at its native size so it stays sharp.
  const heroImage = isHeroImage(snapshot.image) ? snapshot.image : null;
  const compactImage = snapshot.image && !heroImage ? snapshot.image : null;

  return (
    <main className="z-10 mx-5 flex flex-col gap-8 bg-background pb-16 lg:mx-10 2xl:mx-20">
      {/* Hero */}
      <div className="flex flex-col gap-4 pt-6">
        {heroImage ? (
          <img
            src={heroImage.url}
            alt={`${venue}, ${snapshot.city_name}`}
            width={heroImage.width ?? undefined}
            height={heroImage.height ?? undefined}
            loading="eager"
            decoding="async"
            className="mx-auto max-h-72 w-full max-w-3xl rounded-lg object-cover"
          />
        ) : null}
        <H1 className="text-linear-gradient-default text-center font-medium">
          Hotels Near <span className="italic">{venue}</span>
        </H1>
        {compactImage ? (
          <Card className="mx-auto h-auto max-w-xl justify-start gap-0 rounded-lg px-3 py-3 sm:px-3 sm:py-3 md:py-3 lg:py-3">
            <CardContent className="flex items-center gap-4 p-0">
              <img
                src={compactImage.url}
                alt={`${venue}, ${snapshot.city_name}`}
                width={compactImage.width ?? undefined}
                height={compactImage.height ?? undefined}
                loading="eager"
                decoding="async"
                className="h-20 w-36 shrink-0 rounded-md object-cover sm:h-24 sm:w-44"
              />
              <div className="flex min-w-0 flex-col gap-1 text-left">
                <span className="font-medium text-foreground">{venue}</span>
                {address ? (
                  <span className="text-sm text-muted-foreground">
                    {address}
                  </span>
                ) : null}
                {nextEvent?.local_date ? (
                  <span className="text-xs text-muted-foreground">
                    Next: {nextEvent.name} ·{" "}
                    {formatEventDateShort(nextEvent.local_date)}
                  </span>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ) : (
          <H4 className="mx-auto max-w-3xl text-center font-normal text-muted-foreground">
            {address}
          </H4>
        )}
        <p className="mx-auto max-w-3xl text-center text-sm text-muted-foreground">
          {snapshot.description} Rates below are the best points value per hotel
          for stays between{" "}
          {formatEventDateShort(snapshot.checkin_window.start)} and{" "}
          {formatEventDateShort(snapshot.checkin_window.end)}; pick an event for
          rates on that night. Want advanced search?{" "}
          <Button
            variant="link"
            size="none"
            className="italic underline"
            onClick={() => setIsAboutModalOpen(true)}
          >
            Learn more
          </Button>
        </p>
      </div>

      {/* Sort toggle */}
      {hasDistances && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            variant={sortBy === "value" ? "default" : "outline"}
            size="sm"
            onClick={() => setSortBy("value")}
          >
            Best value
          </Button>
          <Button
            variant={sortBy === "distance" ? "default" : "outline"}
            size="sm"
            onClick={() => setSortBy("distance")}
          >
            Closest to {venue}
          </Button>
        </div>
      )}

      {/* Hotel cards grid with the distance caption under each card */}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))" }}
      >
        {hotels.map((hotel, index) => {
          const distance = hotelDistance(snapshot, hotel);
          return (
            <div
              key={hotel.vervotechPropertyId ?? index}
              className="flex flex-col gap-1"
            >
              <HotelCard hotel={hotel} className="w-full" showDate />
              {distance ? (
                <p className="px-1 text-xs text-muted-foreground">
                  {formatDistance(distance)} to {venue}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Upcoming events */}
      {events.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-xl font-semibold text-foreground">
            Upcoming events at {venue}
          </h2>
          <ul className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => {
              const time = formatEventTime(event.local_time);
              return (
                <li key={event.slug}>
                  <Link
                    href={eventPagePath(
                      snapshot.city_slug,
                      snapshot.venue_slug,
                      event.slug
                    )}
                    className="flex h-full flex-col gap-1 rounded-lg border border-border bg-card p-4 transition-colors hover:bg-accent"
                  >
                    <span className="font-medium text-foreground">
                      {event.name}
                    </span>
                    {event.performers.length > 0 || event.organizer ? (
                      <span className="text-sm text-muted-foreground">
                        {event.performers.map((p) => p.name).join(", ")}
                        {event.performers.length > 0 && event.organizer
                          ? " · "
                          : ""}
                        {event.organizer
                          ? `Presented by ${event.organizer}`
                          : ""}
                      </span>
                    ) : null}
                    <span className="text-sm text-muted-foreground">
                      {event.local_date
                        ? formatEventDateShort(event.local_date)
                        : "Date to be announced"}
                      {time ? ` · ${time}` : ""}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {ticketPriceText(event.price_range)
                        ? `${ticketPriceText(event.price_range)} · `
                        : ""}
                      Hotel rates for this night
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Cross-links */}
      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
        <Link
          href={venueCityPath(snapshot.city_slug)}
          className="underline transition-colors hover:text-foreground"
        >
          More venues and events in {snapshot.city_name}
        </Link>
        <Link
          href={`/deals/${snapshot.city_slug}`}
          className="underline transition-colors hover:text-foreground"
        >
          {snapshot.city_name} hotel points deals by month
        </Link>
      </div>

      {/* Concierge handoff: prefills the planner composer; nothing is sent until the user does. */}
      <div className="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center">
        <h2 className="text-xl font-semibold text-foreground">
          Plan this trip with Concierge
        </h2>
        <p className="text-sm text-muted-foreground">
          Tell Concierge your dates and points balances and it will find the
          best way to book a stay near {venue}.
        </p>
        <Link
          href={conciergeHrefFor(
            conciergePromptFor({
              venueName: snapshot.venue.name,
              cityName: snapshot.city_name,
              date: events[0]?.local_date ?? null,
            })
          )}
          className={buttonVariants({ className: "px-8 py-3" })}
        >
          Open Concierge
        </Link>
      </div>

      {/* CTA */}
      <div className="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center">
        <h2 className="text-xl font-semibold text-foreground">
          Search every hotel near {venue}
        </h2>
        <p className="text-sm text-muted-foreground">
          Create a free account to compare points and cash on every hotel near
          the venue for your exact dates.
        </p>
        <Button onClick={() => setIsLoginModalOpen(true)} className="px-8 py-3">
          Get Started Free
        </Button>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Last updated{" "}
        {new Date(snapshot.generated_at).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        })}
      </p>

      <AboutGondolaModal
        open={isAboutModalOpen}
        onOpenChange={setIsAboutModalOpen}
        onClick={() => {
          setIsAboutModalOpen(false);
          setIsLoginModalOpen(true);
        }}
      />
      <GoogleAuthModal
        open={isLoginModalOpen}
        onOpenChange={setIsLoginModalOpen}
        headerText="Create an account"
        callbackUrl="/loyalty-dashboard"
      />
    </main>
  );
}
